import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { Avatar, Button, IconButton, Typography } from "@mui/material";
import { useStore } from "../../helpers";
import { SideOverlay } from "../../components";
import { UserProfile } from "./user-profile";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import { safe_get } from "../../report-visuals";
import { useLocalStorage } from "react-use";
import { Menu, MenuItem, MenuButton } from "@szhsin/react-menu";
import "@szhsin/react-menu/dist/index.css";
import "@szhsin/react-menu/dist/transitions/zoom.css";
import ThumbnailImage from "../../components-new/Thumbnail";
import { ArrowLeftCircleIcon } from "@heroicons/react/24/solid";
import DevicesRoundedIcon from "@mui/icons-material/DevicesRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import ActiveDevices from "./active-devices";

export const labelStyle =
    "w-64 rounded-xl border border-gray-300 bg-white px-4 py-2 text-gray-700 shadow-md mt-1";

export const ProfileMenu = observer(() => {
    const rootStore = useStore();
    const { userStore } = rootStore;
    const [userprofile] = useLocalStorage("__userprofile");
    const [profileImage, setProfileImage] = useState(null);

    const handleClose = () => {
        userStore.set_profile_open(false);
    };

    useEffect(() => {
        setProfileImage(safe_get(userprofile, "profileimageuri", null));
    }, [userprofile]);

    const close_modal = () => {
        rootStore.update_user_profile(false);
    };

    const handleEditProfile = () => {
        rootStore.update_user_profile(true);
        userStore.set_profile_open(false);
    };

    const handleOpenActiveDevices = async () => {
        try {
            const response = await userStore.getactivedevices();

            if (!response?.ok) {
                return;
            }

            const devices = Array.isArray(response?.data?.devices) ? response.data.devices : [];

            userStore.set_active_devices(devices);

            userStore.set_profile_open(false);

            rootStore.current_update_active_devices(true);
        } catch (error) {
            console.error("Failed to fetch active devices", error);

            userStore.set_active_devices([]);
        }
    };

    return (
        <div className={"flex flex-row-reverse items-center gap-2"}>
            <div className="flex flex-col">
                <Typography
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                    }}
                    color="black"
                    variant="button"
                >
                    {userprofile["name"]}
                </Typography>
                <Typography
                    sx={{
                        display: {
                            xs: "none",
                            md: "flex",
                        },
                    }}
                    color="black"
                >
                    {userprofile["id"] === 1 ? null : userprofile["id"]}
                </Typography>
            </div>

            <Menu
                transition
                direction={"right"}
                arrow={true}
                position={"anchor"}
                menuButton={
                    <MenuButton>
                        <div className="flex flex-row gap-2">
                            <Avatar
                                variant="square"
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 2,
                                    display: {
                                        xs: "none",
                                        md: "flex",
                                    },
                                    zIndex: 10,
                                }}
                                alt={userprofile["name"]}
                            >
                                {safe_get(userprofile, "profileimage", null) && (
                                    <ThumbnailImage
                                        s3Key={safe_get(userprofile, "profileimage", null)}
                                        bucket="urn-assets"
                                        width={65}
                                        height={65}
                                    />
                                )}
                            </Avatar>
                        </div>
                    </MenuButton>
                }
                menuClassName={"z-9999 static mx-4 overflow-x-hidden "}
            >
                <MenuItem>
                    <div className="flex flex-col p-2">
                        <div className="flex flex-row justify-between">
                            <Typography variant="h6">User Profile</Typography>
                            <IconButton
                                onClick={handleClose}
                                sx={{
                                    border: "1px #F0F0F0",
                                }}
                                size={"large"}
                            >
                                <CloseIcon className="inline-block" />
                            </IconButton>
                        </div>
                        <div className="flex flex-row items-center gap-x-4">
                            <div>
                                <Avatar
                                    sx={{
                                        width: { xs: "75px", md: "150px" },
                                        height: { xs: "75px", md: "150px" },
                                    }}
                                    alt={safe_get(userprofile, "name", null)}
                                >
                                    {safe_get(userprofile, "profileimage", null) && (
                                        <ThumbnailImage
                                            s3Key={safe_get(userprofile, "profileimage", null)}
                                            bucket="urn-assets"
                                            width={150}
                                            height={150}
                                        />
                                    )}
                                </Avatar>
                            </div>
                            <div className="ml-4 flex flex-col gap-1">
                                <Typography variant="h5">
                                    {safe_get(userprofile, "profile.name", null)}
                                </Typography>
                                <Typography variant="body1">
                                    {safe_get(userprofile, "profile.city", null)}
                                </Typography>
                                <div className="flex flex-row items-center gap-4">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        startIcon={<EditIcon className="text-common-white" />}
                                        onClick={handleEditProfile}
                                    >
                                        Edit
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color="error"
                                        startIcon={<ArrowLeftCircleIcon />}
                                        onClick={() => rootStore.update_logoutModal(true)}
                                    >
                                        Logout
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div className="flex flex-col">
                            <div className="flex flex-row gap-3">
                                <div className="mt-4 flex flex-col">
                                    <Typography variant="body1">Address</Typography>
                                    <p className={labelStyle}>
                                        {safe_get(userprofile, "profile.address", null)}
                                    </p>
                                </div>
                                <div className="mt-4 flex flex-col">
                                    <Typography variant="body1">Phone number</Typography>
                                    <p className={labelStyle}>{safe_get(userprofile, "phonenum", null)}</p>
                                </div>
                            </div>
                            <div className="flex flex-row gap-3">
                                <div className="mt-4 flex flex-col">
                                    <Typography variant="body1">Role</Typography>
                                    <p className={labelStyle}> {safe_get(userprofile, "role", null)}</p>
                                </div>
                                <div className="mt-4 flex flex-col">
                                    <Typography variant="body1">User ID</Typography>
                                    <p className={labelStyle}> {safe_get(userprofile, "id", null)}</p>
                                </div>
                            </div>
                        </div>
                        <div className="mt-5">
                            <button
                                type="button"
                                onClick={handleOpenActiveDevices}
                                className="group flex w-full items-center justify-between rounded-2xl border border-indigo-100 bg-gradient-to-r from-indigo-50 via-white to-blue-50 p-4 transition-all duration-300 hover:-translate-y-[1px] hover:border-indigo-300 hover:shadow-lg"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-100 transition group-hover:bg-indigo-600">
                                        <DevicesRoundedIcon className="text-indigo-700 group-hover:text-white" />
                                    </div>

                                    <div className="text-left">
                                        <p className="font-semibold text-gray-800">Active Devices</p>

                                        <p className="text-xs text-gray-500">Manage logged-in devices</p>
                                    </div>
                                </div>

                                <ChevronRightRoundedIcon className="text-gray-400 transition group-hover:translate-x-1" />
                            </button>
                        </div>
                    </div>
                </MenuItem>
            </Menu>
            <SideOverlay
                children={<UserProfile />}
                onClose={close_modal}
                show={rootStore.active_user_profile}
                title="Edit Profile"
            />
            <SideOverlay
                children={<ActiveDevices />}
                title="Active Devices"
                show={rootStore.active_devices_overlay}
                onClose={() => rootStore.current_update_active_devices(false)}
                size={"large"}
            ></SideOverlay>
        </div>
    );
});
